import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { ArrowForward, UploadFile } from '@mui/icons-material';
import {parse} from 'csv-parse/browser/esm/sync';
import _ from 'lodash';
import useParticipantsForActivity from 'api-new/useParticipantsForActivity';
import LoadingPlaceholder from 'components/shared/LoadingPlaceholder';
import { DateTime, Duration } from 'luxon';
import vlqrApi, { handleError } from 'utils/vlqrApi';
import { useMutation } from '@tanstack/react-query';
import useFocusGroupSchedule from 'api-new/useFocusGroupSchedule';

// Note that translations haven't been specified as of yet. The tool is aimed at internal (staff) users only and
// would be revisited design-wise should we target external users.

const REQUIRED_CSV_COLUMNS = ['Time', 'User', 'Transcript'];

// Primary format matches the CSV produced by the "Download Voice Transcriptions" button
// (e.g. 6/26/2024 9:28:19 PM). The rest are accepted as fallbacks for CSVs downloaded before
// that export was fixed to always use the primary format, or edited/authored by hand.
const TRANSCRIPT_TIME_FORMATS = [
    'M/d/y h:m:s a',
    'M/d/y H:m:s',
    'M/d/y H:m'
];

const parseTranscriptTime = (value) => {
    for (const format of TRANSCRIPT_TIME_FORMATS) {
        const parsed = DateTime.fromFormat(value, format);

        if (parsed.isValid) {
            return parsed;
        }
    }

    return DateTime.fromFormat(value, TRANSCRIPT_TIME_FORMATS[0]);
};

const VoiceTranscriptionsImporter = ({ focusGroupId }) => {
    const [importDidSucceed, setImportDidSucceed] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const { data: focusGroupSchedule, isLoading: isLoadingFocusGroupSchedule } = useFocusGroupSchedule(focusGroupId);
    const { data: focusGroupParticipants, isLoading: isLoadingParticipants } = useParticipantsForActivity(focusGroupId);
    const { mutate: replaceVoiceTranscriptions, isPending: isProcessingRequest } = useMutation({
        mutationFn: voiceTranscriptions => vlqrApi.post(`focusGroups/${focusGroupId}/voiceTranscriptions`, voiceTranscriptions),
        onSuccess: () => {
            setImportDidSucceed(true);
        },
        onError: (error) => {
            setErrorMessage('Something went wrong while importing the transcript. Please try again.');
            handleError(error);
        }
    });

    const [filename, setFilename] = useState(null);
    const [csvData, setCsvData] = useState(null);
    const [csvError, setCsvError] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const transcriptParticipants = _.uniq(csvData?.map(x => x.User));
    const [participantMapping, setParticipantMapping] = useState({});

    // Set via the UI to offset the transcripts. Positive integer indicates how many minutes
    // to subtract from each timestamp. Kept as a string while editing so the field can be validated inline.
    const [transcriptMinutesOffsetInput, setTranscriptMinutesOffsetInput] = useState('0');
    const transcriptMinutesOffset = Number(transcriptMinutesOffsetInput);
    const transcriptMinutesOffsetIsInvalid = transcriptMinutesOffsetInput.trim() === ''
        || !Number.isInteger(transcriptMinutesOffset)
        || transcriptMinutesOffset < 0;
    const transcriptOffsetDuration = Duration.fromMillis((transcriptMinutesOffsetIsInvalid ? 0 : transcriptMinutesOffset) * 60 * 1000);

    const handleFileUpload = (event) => {
        if(!event.target.files) {
            return;
        }

        const file = event.target.files[0];
        const { name } = file;

        const reader = new FileReader();

        reader.onload = (evt) => {
            if (!evt?.target?.result) {
                return;
            }

            setFilename(name);
            setParticipantMapping({});
            setErrorMessage(null);

            // Read in the data, stripping out any BOMs that are present.
            const rawData = evt.target.result.replace(/^\uFEFF/, '');

            let records;
            try {
                records = parse(rawData, {
                    bom: true,
                    columns: true, // ['Time', 'User', 'Transcript']
                    delimiter: ',',
                    trim: true,
                    skip_empty_lines: true
                });
            } catch {
                setCsvData(null);
                setCsvError('This file could not be read as a CSV. Please check the file and try again.');
                return;
            }

            if (records.length > 0) {
                const missingColumns = REQUIRED_CSV_COLUMNS.filter(column => !(column in records[0]));

                if (missingColumns.length > 0) {
                    setCsvData(null);
                    setCsvError(`The CSV is missing required column(s): ${missingColumns.join(', ')}`);
                    return;
                }
            }

            setCsvData(records);
            setCsvError(null);
        };

        reader.readAsText(file);
    };

    if (isLoadingParticipants || isLoadingFocusGroupSchedule) {
        return <LoadingPlaceholder />;
    }

    const focusGroupParticipantIds = focusGroupParticipants.map(x => x.projectParticipantId);
    const focusGroupParticipantsAsObject = _.keyBy(focusGroupParticipants, 'projectParticipantId');
    const isParticipantMapIncomplete = Boolean(transcriptParticipants.find(x => Boolean(participantMapping[x]) === false));
    const actualStartTimeIsMissing = !focusGroupSchedule.actualStartTime;
    const csvHasNoRows = Array.isArray(csvData) && csvData.length === 0;

    // Populate imported transcripts using the session start time as a baseline (ensures that they will be included in reports)
    // Each transcript timestamp will then be adjusted by the same amount
    // Note that transcripts may be adjusted further via 'transcriptMinutesOffset'.
    const openingTranscriptDateTime = csvData?.[0] ? parseTranscriptTime(csvData[0].Time) : null;
    const openingTranscriptDateDiff = (openingTranscriptDateTime?.isValid && !actualStartTimeIsMissing)
        ? DateTime.fromISO(focusGroupSchedule.actualStartTime).diff(openingTranscriptDateTime)
        : null;

    const rowTimestampErrors = (csvData ?? []).reduce((errors, row, index) => {
        if (!parseTranscriptTime(row.Time).isValid) {
            errors.push({ rowNumber: index + 1, value: row.Time });
        }

        return errors;
    }, []);

    const submitIsBlocked = !csvData
        || csvHasNoRows
        || isParticipantMapIncomplete
        || actualStartTimeIsMissing
        || rowTimestampErrors.length > 0
        || transcriptMinutesOffsetIsInvalid;

    const handleSubmit = () => {
        setIsConfirmDialogOpen(false);

        const payload = csvData.map(row => {
            // Dates in csv formatted as follows: e.g. `6/26/2024 9:28:19 PM`
            var timestamp = parseTranscriptTime(row.Time);

            if (openingTranscriptDateDiff) {
                timestamp = timestamp.plus(openingTranscriptDateDiff).minus(transcriptOffsetDuration);
            }

            return {
                participantId: participantMapping[row.User],
                timestamp: timestamp.toUTC().toISO(),
                text: row.Transcript,
                translatedText: null // ignored for now
            };
        });

        replaceVoiceTranscriptions(payload);
    };

    if (importDidSucceed) {
        return (
            <Alert severity='success'>
                <AlertTitle>Done!</AlertTitle>
                Voice transcriptions were imported successfully.
            </Alert>
        );
    }

    return (
        <Stack gap={3}>
            <Box>
                <Typography variant="h6" gutterBottom>Import Transcriptions</Typography>
            </Box>

            { errorMessage &&
                <Alert severity='error'>{errorMessage}</Alert>
            }

            { actualStartTimeIsMissing &&
                <Alert severity='warning'>
                    This session does not have a recorded start time yet, so transcripts cannot be imported until it has actually started.
                </Alert>
            }

            { csvError &&
                <Alert severity='error'>{csvError}</Alert>
            }

            { csvHasNoRows &&
                <Alert severity='warning'>No rows found in CSV. Please check the file and try again.</Alert>
            }

            { rowTimestampErrors.length > 0 &&
                <Alert severity='error'>
                    <AlertTitle>Invalid timestamps found</AlertTitle>
                    The following row(s) have a Time value that could not be parsed (expected format e.g. 6/26/2024 9:28:19 PM):
                    <Box component="ul" sx={{ margin: 0 }}>
                        { rowTimestampErrors.map(({ rowNumber, value }) => (
                            <li key={`row-error-${rowNumber}`}>Row {rowNumber}: &quot;{value}&quot;</li>
                        ))}
                    </Box>
                </Alert>
            }

            <Button
                size="large"
                component="label"
                variant="outlined"
                startIcon={<UploadFile />}
                color={csvData ? 'success' : 'newPrimary' }
            >
                { filename ?? 'Upload CSV' }
                <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
            </Button>
            { transcriptParticipants.length > 0 &&
                <Stack gap={2} sx={{ padding: 2, border: 'thin solid grey', borderColor: 'grey.9', borderRadius: 1 }}>
                    <Typography variant="overline">Map Participants</Typography>
                    <Typography variant="body2">Use the fields below to map transcript names to participants within this session</Typography>
                    <Stack gap={1}>
                        { transcriptParticipants.map(participantName => (
                            <Stack
                                key={`participant-mapper-item-${participantName}`}
                                direction='row'
                                alignItems='center'
                                gap={1}
                            >
                                <Typography variant="subtitle1">
                                    { participantName }
                                </Typography>

                                <ArrowForward />

                                <Autocomplete
                                    size='small'
                                    sx={{ minWidth: '20em' }}
                                    options={focusGroupParticipantIds}
                                    getOptionLabel={(participantId) => focusGroupParticipantsAsObject[participantId].name}
                                    renderInput={(params) => <TextField {...params} placeholder="Choose Target Participant" />}
                                    onChange={(event, value) => setParticipantMapping({ ...participantMapping, [participantName]: value }) }
                                    renderOption={(props, participantId) => {
                                        return (
                                            <li {...props} key={`targetParticipant-list-item-${participantId}`}>
                                                {focusGroupParticipantsAsObject[participantId].name}
                                            </li>
                                        );
                                    }}
                                />
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            }
            <Stack gap={2} sx={{ padding: 2, border: 'thin solid grey', borderColor: 'grey.9', borderRadius: 1 }}>
                <Typography variant="overline">Session Start Time Adjustment</Typography>
                <Typography variant="body2">
                    The timestamp of the first transcript found in the CSV will be overridden to match the ActualStartTime of this session. Other transcripts are then updated such that they remain relative to the first.
                </Typography>

                <Typography variant="body2">
                    This means that by default, all imported transcripts will be included when e.g. generating AI reports.
                </Typography>

                <Typography variant="body2">
                    To customise this, specify a value (in minutes) below which will be subtracted from each timestamp and so simulate a period of &apos;pre-session&apos; discussion, i.e. before the first respondent joined.
                </Typography>

                <Stack direction='row' alignItems='center' gap={1}>
                    <TextField
                        value={transcriptMinutesOffsetInput}
                        onChange={(e) => setTranscriptMinutesOffsetInput(e.target.value)}
                        type="number"
                        error={transcriptMinutesOffsetIsInvalid}
                        helperText={transcriptMinutesOffsetIsInvalid ? 'Enter a non-negative whole number of minutes' : ' '}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                            }
                        }}
                    />
                </Stack>
            </Stack>
            { transcriptParticipants.length > 0 &&
                <Stack direction='row' alignItems='center' gap={1}>
                    <Button
                        variant="contained"
                        sx={{ width: '10em' }}
                        disabled={submitIsBlocked || isProcessingRequest}
                        onClick={() => setIsConfirmDialogOpen(true)}
                    >
                        Submit
                    </Button>
                    { isProcessingRequest && <CircularProgress size={24} /> }
                </Stack>
            }

            <Dialog
                open={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                aria-labelledby="voice-transcription-import-confirm-title"
                aria-describedby="voice-transcription-import-confirm-description"
            >
                <DialogTitle id="voice-transcription-import-confirm-title">Replace existing transcriptions?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="voice-transcription-import-confirm-description">
                        Importing this file will replace any existing voice transcriptions for this session. This cannot be undone. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Replace Transcriptions
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

VoiceTranscriptionsImporter.propTypes = {
    focusGroupId: PropTypes.string
};

export default VoiceTranscriptionsImporter;
